import { HsvColor } from 'react-colorful';

export function hexToHsv(hex: string): HsvColor {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let hue = 0;
  if (d > 0) {
    if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) hue = ((b - r) / d + 2) / 6;
    else hue = ((r - g) / d + 4) / 6;
  }
  return {
    h: Math.round(hue * 360),
    s: Math.round(max > 0 ? (d / max) * 100 : 0),
    v: Math.round(max * 100)
  };
}

export function hsvToHex(h: number, s: number, v: number): string {
  const sv = s / 100;
  const vv = v / 100;
  const k = (n: number) => (n + h / 60) % 6;
  const f = (n: number) =>
    vv - vv * sv * Math.max(0, Math.min(k(n), 4 - k(n), 1));
  const toHex = (n: number) =>
    Math.round(n * 255)
      .toString(16)
      .padStart(2, '0');
  return '#' + toHex(f(5)) + toHex(f(3)) + toHex(f(1));
}

export function hsvToRgb(
  h: number,
  s: number,
  v: number
): { r: number; g: number; b: number } {
  const hex = hsvToHex(h, s, v);
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16)
  };
}

export function hsvToHsl(
  h: number,
  s: number,
  v: number
): { h: number; s: number; l: number } {
  const sv = s / 100;
  const vv = v / 100;
  const l = vv * (1 - sv / 2);
  const sl = l === 0 || l === 1 ? 0 : (vv - l) / Math.min(l, 1 - l);
  return {
    h: Math.round(h),
    s: Math.round(sl * 100),
    l: Math.round(l * 100)
  };
}

export function rgbToHsv(r: number, g: number, b: number): HsvColor {
  const hex =
    '#' +
    [r, g, b]
      .map((c) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0'))
      .join('');
  return hexToHsv(hex);
}

export function hslToHsv(hn: number, s: number, l: number): HsvColor {
  const sl = s / 100;
  const ll = l / 100;
  const a = sl * Math.min(ll, 1 - ll);
  const f = (n: number) => {
    const k = (n + hn / 30) % 12;
    return ll - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  const hex =
    '#' +
    [f(0), f(8), f(4)]
      .map((c) =>
        Math.round(c * 255)
          .toString(16)
          .padStart(2, '0')
      )
      .join('');
  return hexToHsv(hex);
}
